/**
 * 刊登标题译英：含中文时走 DeepSeek（/listing-export/translate-title）。
 * SHEIN / TEMU / TikTok 共用翻译能力；失败文案由调用方按平台传入。
 */

import { listingExportTranslateAPI } from '@/utils/api';
import { textLooksLikeLanguage } from '@/utils/exportTitleTranslate';

const titleCache = new Map();
const CJK_RE = /[\u3400-\u9fff]/;
const TRANSLATE_MAX_ATTEMPTS = 3;

export const DEFAULT_ENGLISH_TITLE_FAIL_MESSAGE =
  '标题含中文且 DeepSeek 未能译成英文，英文名称不能包含中文';
export const SHEIN_ENGLISH_TITLE_FAIL_MESSAGE =
  '标题含中文且 DeepSeek 未能译成英文，SHEIN 产品标题不能包含中文';
export const TEMU_ENGLISH_TITLE_FAIL_MESSAGE =
  '标题含中文且 DeepSeek 未能译成英文，TEMU 英文名称不能包含中文';
export const TIKTOK_ENGLISH_TITLE_FAIL_MESSAGE =
  '标题含中文且 DeepSeek 未能译成英文，TikTok 英文名称不能包含中文';

export function clearEnglishListingTitleCache() {
  titleCache.clear();
}

function asTitle(value) {
  return String(value || '').trim();
}

export function titleContainsCjk(text) {
  return CJK_RE.test(asTitle(text));
}

export function isLikelyEnglishListingTitle(text) {
  const title = asTitle(text);
  if (!title) return true;
  if (titleContainsCjk(title)) return false;
  // 纯数字/符号（如规则 ID「9」）无拉丁字母：不当英文标题，避免英→中误译
  if (!/[A-Za-zÀ-ÿ]/.test(title)) return false;
  return textLooksLikeLanguage(title, 'en');
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientTranslateError(err) {
  const status = Number(err?.status || err?.response?.status || 0);
  const msg = String(err?.message || err?.userMessage || err?.response?.data?.message || '').toLowerCase();
  if (status === 502 || status === 504 || status === 503) return true;
  return /连接被重置|connection reset|超时|timeout|稍后重试|network/.test(msg);
}

/**
 * 已是英文则原样返回；含中文则调用 DeepSeek 译成英文。
 * @param {string} title
 * @param {{ failMessage?: string }} [options]
 * @returns {Promise<string>}
 */
export async function ensureEnglishListingTitle(title, options = {}) {
  const text = asTitle(title);
  if (!text) return '';
  if (!titleContainsCjk(text) && isLikelyEnglishListingTitle(text)) return text;
  if (titleCache.has(text)) {
    const cached = titleCache.get(text);
    if (cached && !titleContainsCjk(cached)) return cached;
  }

  const failMessage = String(options.failMessage || '').trim()
    || DEFAULT_ENGLISH_TITLE_FAIL_MESSAGE;

  let lastErr = null;
  for (let attempt = 1; attempt <= TRANSLATE_MAX_ATTEMPTS; attempt += 1) {
    try {
      const result = await listingExportTranslateAPI.translate(
        text,
        'en',
        titleContainsCjk(text) ? 'zh' : '',
      );
      const translated = asTitle(result?.translatedText || result?.data?.translatedText);
      if (translated && !titleContainsCjk(translated)) {
        titleCache.set(text, translated);
        return translated;
      }
      lastErr = new Error('DeepSeek 译文无效或仍含中文');
    } catch (err) {
      lastErr = err;
      if (!isTransientTranslateError(err) || attempt >= TRANSLATE_MAX_ATTEMPTS) {
        break;
      }
      await sleep(400 * attempt);
    }
  }
  const detail = String(lastErr?.message || lastErr?.userMessage || '').trim();
  if (detail && !detail.includes(failMessage)) {
    throw new Error(`${failMessage}（${detail}）`);
  }
  throw new Error(failMessage);
}
